import { Injectable } from '@angular/core';
import { Headers, RequestOptions, Response, ResponseContentType } from '@angular/http';
// tslint:disable-next-line:import-blacklist
import { Observable } from 'rxjs';

import { AppSettings } from '../../app.settings';
import { HttpClientService } from './http.client.service';
import { LoadingService } from './loading.service';

declare const swal: any;

export interface ApiRequestOptions {
    secure?: boolean;
    params?: { [key: string]: any };
    responseType?: ResponseContentType;
    headers?: Headers;
    showSpinner?: boolean;
    handleError?: boolean;
}
@Injectable()
export class ApiClientService {
    constructor(private http: HttpClientService, private loadingService: LoadingService) { }

    private createRequestOptions(options: ApiRequestOptions = {}): RequestOptions {
        const requestOptions = new RequestOptions();

        if (options.responseType !== undefined) {
            requestOptions.responseType = options.responseType;
        }

        if (options.headers) {
            requestOptions.headers = options.headers;
        }
        return requestOptions;
    }

    /**
     * - Maneja errores HTTP de forma centralizada.
     * - Muestra el mensaje de error al usuario.
     * - Permite inferir el tipo de respuesta usando genéricos.
     *
     * Ejemplo:
     * this.apiClient.apiGet<MyDto>('/endpoint', { params: { id: 1 } });
     */
    apiGet<T>(uri: string, options: ApiRequestOptions = {}) {
        const secure = options.secure !== undefined ? options.secure : true;
        const showSpinner = options.showSpinner !== undefined ? options.showSpinner : true;
        const handleError = options.handleError !== undefined ? options.handleError : true;
        const requestOptions = this.createRequestOptions(options);

        if (showSpinner) {
            this.loadingService.enableShowSpinner();
        }

        const url = this.buildUrl(uri, secure) +
            (options.params ? this.buildQueryParams(options.params) : '');

        const request = this.http.get(url, requestOptions);
        const handledRequest = handleError ? request.catch(error => this.handleHttpError(error)) : request;

        return handledRequest
            .map((res: Response) => res.json() as T)
            .finally(() => {
                if (showSpinner) {
                    this.loadingService.disableShowSpinner();
                }
            });
    }

    /**
     * Realiza un GET contra la API esperando una respuesta Blob.
     *
     * Este método:
     * - Construye la URL segura por defecto.
     * - Permite enviar query params.
     * - Maneja errores HTTP de forma centralizada.
     * - Devuelve directamente el contenido como Blob.
     */
    apiGetBlob(uri: string, options: ApiRequestOptions = {}) {
        const secure = options.secure !== undefined ? options.secure : true;
        const showSpinner = options.showSpinner !== undefined ? options.showSpinner : true;

        const requestOptions = this.createRequestOptions(Object.assign({}, options, {
            responseType: ResponseContentType.Blob
        }));

        if (showSpinner) {
            this.loadingService.enableShowSpinner();
        }

        const url = this.buildUrl(uri, secure) +
            (options.params ? this.buildQueryParams(options.params) : '');

        return this.http
            .get(url, requestOptions)
            .catch(error => this.handleHttpError(error))
            .map((res: Response) => res.blob())
            .finally(() => {
                if (showSpinner) {
                    this.loadingService.disableShowSpinner();
                }
            });
    }

    /**
     * Maneja errores de forma centralizada, controla spinner,
     * permite query params y permite inferir el tipo de respuesta.
     */
    apiPost<T>(uri: string, body: any, options: ApiRequestOptions = {}) {
        const secure = options.secure !== undefined ? options.secure : true;
        const showSpinner = options.showSpinner !== undefined ? options.showSpinner : true;
        const handleError = options.handleError !== undefined ? options.handleError : true;
        const requestOptions = this.createRequestOptions(options);

        if (showSpinner) {
            this.loadingService.enableShowSpinner();
        }

        const url = this.buildUrl(uri, secure) + (options.params ? this.buildQueryParams(options.params) : '');
        const request = this.http.post(url, body, requestOptions);
        const handledRequest = handleError ? request.catch(error => this.handleHttpError(error)) : request;

        return handledRequest
            .map((res: Response) => res.json() as T)
            .finally(() => {
                if (showSpinner) {
                    this.loadingService.disableShowSpinner();
                }
            });
    }

    /**
     * Maneja errores de forma centralizada, controla spinner,
     * permite query params y permite inferir el tipo de respuesta.
     */
    apiDelete<T>(uri: string, options: ApiRequestOptions = {}) {
        const secure = options.secure !== undefined ? options.secure : true;
        const showSpinner = options.showSpinner !== undefined ? options.showSpinner : true;
        const handleError = options.handleError !== undefined ? options.handleError : true;
        const requestOptions = this.createRequestOptions(options);

        if (showSpinner) {
            this.loadingService.enableShowSpinner();
        }

        const url = this.buildUrl(uri, secure) + (options.params ? this.buildQueryParams(options.params) : '');
        const request = this.http.delete(url, requestOptions);
        const handledRequest = handleError ? request.catch(error => this.handleHttpError(error)) : request;

        return handledRequest
            .map((res: Response) => res.json() as T)
            .finally(() => {
                if (showSpinner) {
                    this.loadingService.disableShowSpinner();
                }
            });
    }

    /**
     * Maneja errores de forma centralizada, controla spinner,
     * permite query params y permite inferir el tipo de respuesta.
     */
    apiPut<T>(uri: string, body: any, options: ApiRequestOptions = {}) {
        const secure = options.secure !== undefined ? options.secure : true;
        const showSpinner = options.showSpinner !== undefined ? options.showSpinner : true;
        const handleError = options.handleError !== undefined ? options.handleError : true;
        const requestOptions = this.createRequestOptions(options);

        if (showSpinner) {
            this.loadingService.enableShowSpinner();
        }

        const url = this.buildUrl(uri, secure) + (options.params ? this.buildQueryParams(options.params) : '');
        const request = this.http.put(url, body, requestOptions);
        const handledRequest = handleError ? request.catch(error => this.handleHttpError(error)) : request;

        return handledRequest
            .map((res: Response) => res.json() as T)
            .finally(() => {
                if (showSpinner) {
                    this.loadingService.disableShowSpinner();
                }
            });
    }

    /**
     * @deprecated Usar apiGet<T>().
     * apiGet<T>() maneja errores de forma centralizada, controla spinner,
     * permite query params y permite inferir el tipo de respuesta.
     */
    get(uri: string, options: any = { asJson: true, secure: true }) {
        return this.toJSON(this.http.get(this.buildUrl(uri, options.secure)), options.asJson);
    }

    /**
     * @deprecated Usar apiGetBlob() o apiGet<T>() según corresponda.
     * Los métodos api* manejan errores de forma centralizada y controlan spinner.
     */
    getNotJSON(uri: string, options: any = { asJson: true, secure: true }) {
        return this.http.get(this.buildUrl(uri, options.secure));
    }


    /**
     * @deprecated Usar apiPost<T>().
     * apiPost<T>() maneja errores de forma centralizada, controla spinner,
     * permite query params y permite inferir el tipo de respuesta.
     */
    post(uri: string, body: any, options: any = { asJson: true, secure: true }) {
        return this.toJSON(this.http.post(this.buildUrl(uri, options.secure), body), options.asJson);
    }

    /**
     * @deprecated Usar apiPost<T>() cuando sea posible.
     * Este método no usa el manejo centralizado de errores ni spinner.
     */
    postFiles(uri: string, body: any, options: any = { asJson: true, secure: true }) {

        const headerse = new Headers();
        // headerse.append('Content-Type', 'text/html; charset=utf-8');
        headerse.delete('Content-Type');

        // headerse.append('Accept', 'application/json');
        const optionse = new RequestOptions({ headers: headerse });

        return this.http.post(this.buildUrl(uri, options.secure), body, optionse);
    }

    /**
   * @deprecated Usar apiPut<T>().
   * apiPut<T>() maneja errores de forma centralizada, controla spinner,
   * permite query params y permite inferir el tipo de respuesta.
   */
    put(uri: string, body: any, options: any = { asJson: true, secure: true }) {
        return this.toJSON(this.http.put(this.buildUrl(uri, options.secure), body), options.asJson);
    }

    /**
     * @deprecated Usar apiDelete<T>().
     * apiDelete<T>() maneja errores de forma centralizada, controla spinner,
     * permite query params y permite inferir el tipo de respuesta.
     */
    delete(uri: string, options: any = { asJson: true, secure: true }) {
        return this.toJSON(this.http.delete(this.buildUrl(uri, options.secure)), options.asJson);
    }

    private toJSON(obs: Observable<Response>, asJson: boolean) {
        if (asJson === false) {
            return obs;
        } else {
            return obs.map((response: Response) => response.json());
        }
    }

    private buildQueryParams(params: { [key: string]: any }): string {
        const query = Object.keys(params)
            .filter(k => params[k] !== null && params[k] !== undefined && params[k] !== '')
            .map(k => encodeURIComponent(k) + '=' + encodeURIComponent(params[k]))
            .join('&');

        return query ? '?' + query : '';
    }

    private buildUrl(uri: string, secure: boolean) {
        if (secure === false) {
            return AppSettings.API_ENDPOINT + uri;
        } else {
            return AppSettings.API_ENDPOINT + '/secure/educationalCenter/' + this.getEducationalCenterId() + '/level/' + this.getLevelId() + uri;
        }
    }

    public getEducationalCenterId() {
        const educationalCenterId: number = JSON.parse(localStorage.getItem('educationalCenters')) ?
            JSON.parse(localStorage.getItem('educationalCenters')).id : '';
        return educationalCenterId;
    }

    public getLevelId() {
        const levelId: number = JSON.parse(localStorage.getItem('educationalCenters')) &&
            JSON.parse(localStorage.getItem('educationalCenters')).level ?
            JSON.parse(localStorage.getItem('educationalCenters')).level.id : '';
        return levelId;
    }

    private handleHttpError(error: any) {
        let message = 'Ocurrió un error inesperado.';
        let status = 0;
        let body: any = null;

        if (error) {
            status = error.status || 0;

            try {
                body = error.json ? error.json() : null;
            } catch (e) {
                body = null;
            }

            if (body && body.message) {
                message = body.message;
            }
        }

        swal({
            title: 'Atención!',
            text: message,
            type: 'warning',
            confirmButtonColor: '#b8465e'
        });

        return Observable.throw({
            status: status,
            message: message,
            originalError: error,
            body: body
        });
    }
}


// WEBPACK FOOTER //
// D:/Projects/GitHub/education-ee-web/src/app/services/util/api.client.service.ts